import type { LocalizedText } from "../i18n/i18n";
import type { TemplateVariable } from "./templateVariables";

export type TemplateFormulaValueType = "date" | "number" | "link" | "text";

export type TemplateFormulaDefinition = {
  value: string;
  labelKey: LocalizedText;
  valueType: Exclude<TemplateFormulaValueType, "text">;
};

export type TemplateTokenAtCursor = {
  key: string;
  start: number;
  end: number;
  text: string;
  valueType: TemplateFormulaValueType;
};

const DATE_VARIABLE_KEYS = new Set([
  "dateOfBirth",
  "mandateSignedDate",
  "contractStartDate",
  "contractEndDate",
  "cancellationDate",
  "restPeriodRequestDate",
  "restPeriodStartDate",
  "restPeriodEndDate",
  "restPeriodNextContributionDueDate",
  "currentDate",
  "reminder.openItem.dueDate",
  "appointmentDate"
]);

const NUMBER_VARIABLE_KEYS = new Set([
  "contractPrice",
  "contractMainTermTotalAmount",
  "contractPaidAmount",
  "contractRemainingAmount",
  "restPeriodMonthlyFee",
  "restPeriodFeeTotalAmount",
  "restPeriodNextContributionAmount",
  "restPeriodWeeksCount",
  "currentYear",
  "currentMonth",
  "currentDay",
  "appointmentDurationMinutes",
  "reminder.openItem.amount",
  "reminder.principalAmount",
  "reminder.feeAmount",
  "reminder.totalAmount",
  "reminder.regularOpenAmount",
  "reminder.chargebackOpenAmount",
  "reminder.chargebackFeeOpenAmount",
  "reminder.personTotalOpenAmount",
  "debtCollection.totalAmount",
  "debtCollection.openItemCount"
]);

export const templateFormulaDefinitions: TemplateFormulaDefinition[] = [
  { value: "link", labelKey: "Formula option link", valueType: "link" },
  { value: "link=\"klick\"", labelKey: "Formula option link with text", valueType: "link" },
  { value: "+ 1d", labelKey: "Formula option plus 1 day", valueType: "date" },
  { value: "+ 3d", labelKey: "Formula option plus 3 days", valueType: "date" },
  { value: "+ 1w", labelKey: "Formula option plus 1 week", valueType: "date" },
  { value: "+ 3w", labelKey: "Formula option plus 3 weeks", valueType: "date" },
  { value: "+ 1m", labelKey: "Formula option plus 1 month", valueType: "date" },
  { value: "+ 1y", labelKey: "Formula option plus 1 year", valueType: "date" },
  { value: "- 1d", labelKey: "Formula option minus 1 day", valueType: "date" },
  { value: "- 1w", labelKey: "Formula option minus 1 week", valueType: "date" },
  { value: "- 1m", labelKey: "Formula option minus 1 month", valueType: "date" },
  { value: "- 10%", labelKey: "Formula option minus 10 percent", valueType: "number" },
  { value: "+ 10%", labelKey: "Formula option plus 10 percent", valueType: "number" },
  { value: "* 10%", labelKey: "Formula option 10 percent of", valueType: "number" },
  { value: "+ 5", labelKey: "Formula option plus 5", valueType: "number" },
  { value: "- 5", labelKey: "Formula option minus 5", valueType: "number" },
  { value: "* 2", labelKey: "Formula option times 2", valueType: "number" },
  { value: "/ 2", labelKey: "Formula option divided by 2", valueType: "number" }
];

const TEMPLATE_TOKEN_REGEX = /\{\{\s*(?<bracedKey>[A-Za-z][A-Za-z0-9_.:]*)(?:\s+(?:[+\-*/]\s+[^{}]+|link(?:\s*=\s*(?:"[^"]*"|'[^']*'|&quot;.*?&quot;))?))?\s*\}\}/g;

export function normalizeTemplateFormulaPart(formulaPart: string): string {
  return formulaPart.trim().replace(/\s+/g, " ");
}

export function buildTemplateVariableToken(key: string, formulaPart: string): string {
  const formula = normalizeTemplateFormulaPart(formulaPart);
  return formula ? `{{${key} ${formula}}}` : `{{${key}}}`;
}

/**
 * Katalogeinträge mit `link` sind nur die Auswahl. Gespeichert wird die Formel am Zielschlüssel,
 * sonst bleibt `{{editBookingLink}}` im Text stehen — das Backend kennt den Schlüssel nicht.
 */
export function buildInsertedTemplateToken(variable: TemplateVariable, linkLabel?: string): string {
  if (!variable.link) return buildTemplateVariableToken(variable.key, "");
  const label = (linkLabel ?? "").trim();
  return buildTemplateVariableToken(variable.link.targetKey, label ? `link="${label}"` : "link");
}

export function getTemplateVariableFormulaType(key: string): TemplateFormulaValueType {
  if (DATE_VARIABLE_KEYS.has(key)) return "date";
  if (NUMBER_VARIABLE_KEYS.has(key)) return "number";
  if (isLinkFormulaKey(key)) return "link";
  return "text";
}

// Schlüssel der Backend-Platzhalter (kleingeschrieben verglichen), die Links liefern.
const LINK_FORMULA_KEY_RESET = "resetlink";
const LINK_FORMULA_KEY_APPOINTMENT_BOOKING = "terminbuchunglink";

function isLinkFormulaKey(key: string): boolean {
  const name = key.toLowerCase();
  return name.endsWith("url")
    || name.endsWith("email")
    || name.endsWith("website")
    || name === LINK_FORMULA_KEY_RESET
    || name === LINK_FORMULA_KEY_APPOINTMENT_BOOKING;
}

export function findTemplateTokenAtCursor(text: string, cursorPosition: number): TemplateTokenAtCursor | null {
  const normalizedCursor = Math.max(0, Math.min(cursorPosition, text.length));
  TEMPLATE_TOKEN_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TEMPLATE_TOKEN_REGEX.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (normalizedCursor < start || normalizedCursor > end) continue;

    const key = match.groups?.bracedKey;
    if (!key) return null;

    return {
      key,
      start,
      end,
      text: match[0],
      valueType: getTemplateVariableFormulaType(key)
    };
  }

  return null;
}
